import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

export default function AcademyLesson() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quizMode, setQuizMode] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState({});
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [xpEarned, setXpEarned] = useState(0);
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";

  useEffect(() => {
    fetch(`/api/academy/lessons/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(res => {
      if (res.ok) {
        setLesson(res.data);
        setQuestions(res.questions || []);
        if (res.data.completed) setCompleted(true);
      }
      setLoading(false);
    });
  }, [id]);

  const handleAnswer = async (questionId, answerIdx) => {
    if (submitted) return;
    setAnswers(prev => ({ ...prev, [questionId]: answerIdx }));

    const res = await fetch(`/api/academy/quiz/${questionId}/answer`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ answer_idx: answerIdx })
    }).then(r => r.json());

    if (res.ok) {
      setResults(prev => ({ ...prev, [questionId]: res }));
    }
  };

  const handleComplete = async () => {
    if (completing || completed) return;
    setCompleting(true);
    const score = Object.values(results).filter(r => r.correct).length;
    const res = await fetch(`/api/academy/lessons/${id}/complete`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ score, time_spent: 300 })
    }).then(r => r.json());

    if (res.ok) {
      setCompleted(true);
      setXpEarned(res.xp_earned || 0);
    }
    setCompleting(false);
  };

  const allAnswered = questions.length === 0 || questions.every(q => answers[q.id] !== undefined);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 400 }}>
      <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Loading lesson...</div>
    </div>
  );

  if (!lesson) return (
    <div className="page"><p style={{ color: "var(--text-muted)" }}>Lesson not found.</p></div>
  );

  const content = lesson.content || {};

  const renderContent = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const elements = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      // Code block
      if (line.startsWith('```')) {
        const codeLines = [];
        i++;
        while (i < lines.length && !lines[i].startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        elements.push(
          <pre key={i} style={{
            background: "var(--bg-elevated)", borderRadius: "var(--radius-md)",
            padding: "16px 20px", margin: "16px 0", fontFamily: "monospace",
            fontSize: 13, color: "var(--accent)", whiteSpace: "pre",
            overflowX: "auto", lineHeight: 1.6,
            border: "1px solid var(--border)"
          }}>{codeLines.join('\n')}</pre>
        );
        i++;
        continue;
      }
      // H2
      if (line.startsWith('## ')) {
        elements.push(<h2 key={i} style={{ fontSize: 17, fontWeight: 700, color: "var(--accent)", margin: "24px 0 10px" }}>{line.replace('## ', '')}</h2>);
        i++; continue;
      }
      // H3
      if (line.startsWith('### ')) {
        elements.push(<h3 key={i} style={{ fontSize: 15, fontWeight: 700, color: "var(--text-main)", margin: "20px 0 8px" }}>{line.replace('### ', '')}</h3>);
        i++; continue;
      }
      // Empty line
      if (!line.trim()) {
        elements.push(<div key={i} style={{ height: 10 }} />);
        i++; continue;
      }
      // Bold only line
      if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
        elements.push(<p key={i} style={{ fontSize: 14, fontWeight: 700, color: "var(--text-main)", margin: "12px 0 4px" }}>{line.replace(/\*\*/g, '')}</p>);
        i++; continue;
      }
      // Success/danger lines
      if (line.startsWith('  ') || line.startsWith('  ')) {
        elements.push(<p key={i} style={{ fontSize: 13, color: line.startsWith('  ') ? "var(--success)" : "var(--danger)", margin: "4px 0", lineHeight: 1.6 }}>{line}</p>);
        i++; continue;
      }
      // Inline bold
      if (line.includes('**')) {
        const parts = line.split(/(\*\*.*?\*\*)/g);
        elements.push(
          <p key={i} style={{ fontSize: 14, color: "var(--text-muted)", margin: "4px 0", lineHeight: 1.8 }}>
            {parts.map((part, pi) =>
              part.startsWith('**') && part.endsWith('**')
                ? <strong key={pi} style={{ color: "var(--text-main)" }}>{part.replace(/\*\*/g, '')}</strong>
                : part
            )}
          </p>
        );
        i++; continue;
      }
      // Normal paragraph
      elements.push(<p key={i} style={{ fontSize: 14, color: "var(--text-muted)", margin: "4px 0", lineHeight: 1.8 }}>{line}</p>);
      i++;
    }
    return elements;
  };

  return (
    <div className="page" style={{ maxWidth: 760, margin: "0 auto" }}>
      {/* Back */}
      <button onClick={() => navigate(-1)}
        style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 13, padding: "0 0 16px", display: "flex", alignItems: "center", gap: 6 }}>
        ← Back
      </button>

      {/* Lesson Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "var(--text-muted)", background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: 99, padding: "2px 10px" }}>
            {lesson.type}
          </span>
          <span style={{ fontSize: 11, color: "var(--text-muted)", background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: 99, padding: "2px 10px" }}>
            ⏱ {lesson.duration} min
          </span>
          <span style={{ fontSize: 11, color: "#FFD700", background: "#FFD70022", border: "1px solid #FFD70044", borderRadius: 99, padding: "2px 10px" }}>
               {lesson.xp_reward} XP
          </span>
          {completed && (
            <span style={{ fontSize: 11, color: "var(--success)", background: "rgba(29,209,161,0.1)", border: "1px solid var(--success)", borderRadius: 99, padding: "2px 10px", fontWeight: 700 }}>
                 Completed
            </span>
          )}
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--text-main)", lineHeight: 1.3 }}>
          {lesson.title}
        </h1>
      </div>

      {/* XP Earned Toast */}
      {xpEarned > 0 && (
        <div style={{
          background: "#FFD70022", border: "1px solid #FFD700",
          borderRadius: "var(--radius-lg)", padding: "12px 20px", marginBottom: 20,
          display: "flex", alignItems: "center", gap: 10
        }}>
          <span style={{ fontSize: 24 }}></span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#FFD700" }}>Lesson Complete!</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>You earned   {xpEarned} XP</div>
          </div>
        </div>
      )}

      {/* Content */}
      {!quizMode && (
        <div style={{
          background: "var(--bg-surface)", border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)", padding: "28px 32px", marginBottom: 24
        }}>
          {content.content && content.content.split('\n').map((line, i) => {
            if (line.startsWith('## ')) return (
              <h2 key={i} style={{ fontSize: 17, fontWeight: 700, color: "var(--accent)", margin: "20px 0 10px" }}>
                {line.replace('## ', '')}
              </h2>
            );
            if (line.startsWith('**') && line.endsWith('**')) return (
              <p key={i} style={{ fontSize: 14, fontWeight: 700, color: "var(--text-main)", margin: "12px 0 4px" }}>
                {line.replace(/\*\*/g, '')}
              </p>
            );
            if (line.startsWith('```')) return (
              <div key={i} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "12px 16px", margin: "12px 0", fontFamily: "monospace", fontSize: 12, color: "var(--text-muted)", whiteSpace: "pre-wrap" }}>
                {line.replace(/```/g, '')}
              </div>
            );
            if (line.startsWith('  ') || line.startsWith('  ')) return (
              <p key={i} style={{ fontSize: 13, color: line.startsWith('  ') ? "var(--success)" : "var(--danger)", margin: "4px 0", lineHeight: 1.6 }}>
                {line}
              </p>
            );
            if (!line.trim()) return <div key={i} style={{ height: 8 }} />;
            // Handle **bold** inline
            if (line.includes('**')) {
              const parts = line.split(/(\*\*.*?\*\*)/g);
              return (
                <p key={i} style={{ fontSize: 14, color: "var(--text-muted)", margin: "4px 0", lineHeight: 1.8 }}>
                  {parts.map((part, pi) =>
                    part.startsWith('**') && part.endsWith('**')
                      ? <strong key={pi} style={{ color: "var(--text-main)" }}>{part.replace(/\*\*/g, '')}</strong>
                      : part
                  )}
                </p>
              );
            }
            return (
              <p key={i} style={{ fontSize: 14, color: "var(--text-muted)", margin: "4px 0", lineHeight: 1.8 }}>
                {line}
              </p>
            );
          })}

          {/* Key Points */}
          {content.key_points && content.key_points.length > 0 && (
            <div style={{
              marginTop: 24, background: "var(--accent-soft)", border: "1px solid var(--accent-border)",
              borderRadius: "var(--radius-lg)", padding: "16px 20px"
            }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--accent)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                 Key Points
              </div>
              {content.key_points.map((kp, i) => (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                  <span style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }}>→</span>
                  <span style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.6 }}>{kp}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quiz Mode */}
      {quizMode && questions.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--text-main)" }}>
             Knowledge Check — {questions.length} question{questions.length > 1 ? "s" : ""}
          </h2>
          {questions.map((q, qi) => {
            const result = results[q.id];
            const chosen = answers[q.id];
            return (
              <div key={q.id} style={{
                background: "var(--bg-surface)", border: "1px solid var(--border)",
                borderRadius: "var(--radius-xl)", padding: "20px 24px"
              }}>
                <p style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 600, color: "var(--text-main)", lineHeight: 1.5 }}>
                  <span style={{ color: "var(--text-muted)", marginRight: 8 }}>Q{qi + 1}.</span>
                  {q.question}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {JSON.parse(q.options).map((opt, oi) => {
                    let bg = "var(--bg-elevated)", border = "var(--border)", color = "var(--text-muted)";
                    if (result) {
                      if (oi === result.correct_idx) { bg = "rgba(29,209,161,0.1)"; border = "var(--success)"; color = "var(--success)"; }
                      else if (oi === chosen && oi !== result.correct_idx) { bg = "rgba(255,92,122,0.1)"; border = "var(--danger)"; color = "var(--danger)"; }
                    } else if (chosen === oi) { bg = "var(--accent-soft)"; border = "var(--accent)"; color = "var(--accent)"; }

                    return (
                      <button key={oi} onClick={() => handleAnswer(q.id, oi)}
                        style={{
                          background: bg, border: `1.5px solid ${border}`, borderRadius: "var(--radius-md)",
                          padding: "12px 16px", color, textAlign: "left", cursor: result ? "default" : "pointer",
                          fontSize: 13, transition: "all 0.15s", fontFamily: "inherit"
                        }}>
                        <span style={{ color: "var(--text-muted)", marginRight: 8 }}>{String.fromCharCode(65 + oi)}.</span>
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {result && (
                  <div style={{
                    marginTop: 12, background: "var(--bg-elevated)", borderRadius: "var(--radius-md)",
                    padding: "10px 14px", borderLeft: `3px solid ${result.correct ? "var(--success)" : "var(--danger)"}`
                  }}>
                    <p style={{ margin: 0, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6 }}>
                      <strong style={{ color: result.correct ? "var(--success)" : "var(--danger)" }}>
                        {result.correct ? "   Correct!" : "   Incorrect."}
                      </strong>{" "}
                      {result.explanation}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {questions.length > 0 && !quizMode && (
          <button onClick={() => setQuizMode(true)}
            style={{ padding: "11px 24px", background: "transparent", border: "1.5px solid var(--accent)", color: "var(--accent)", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
             Take Quiz ({questions.length} questions)
          </button>
        )}
        {quizMode && (
          <button onClick={() => setQuizMode(false)}
            style={{ padding: "11px 24px", background: "transparent", border: "1.5px solid var(--border)", color: "var(--text-muted)", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: 14 }}>
            ← Back to Lesson
          </button>
        )}
        {!completed && (allAnswered || questions.length === 0) && (
          <button onClick={handleComplete} disabled={completing}
            style={{ padding: "11px 24px", background: "var(--accent)", border: "none", color: "#fff", borderRadius: "var(--radius-md)", cursor: completing ? "wait" : "pointer", fontSize: 14, fontWeight: 600 }}>
            {completing ? "Saving..." : "   Mark as Complete"}
          </button>
        )}
        {completed && (
          <button onClick={() => navigate(-1)}
            style={{ padding: "11px 24px", background: "var(--success)", border: "none", color: "#fff", borderRadius: "var(--radius-md)", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
            ← Back to Course
          </button>
        )}
      </div>
    </div>
  );
}
